import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { tokens } from "../../theme";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ProductionQuantityLimitsOutlinedIcon from "@mui/icons-material/ProductionQuantityLimitsOutlined";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";
import ProgressCircle from "../../components/ProgressCircle";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

const abbr2 = (name = "Unknown") => name.trim().slice(0, 2).toUpperCase();

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dashboard`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchDashboard();
}, []);

  // ✅ SAFE destructuring (prevents crash)
  const summary = data?.summary ?? {};
  const recent = data?.recent ?? {};
  const salesRows = recent?.sales ?? [];

  const barData = useMemo(() => {
    const sums = new Map();

    for (const s of salesRows) {
      const name = s?.Item?.name ?? "Unknown";
      const amt = Number.parseFloat(s?.quantity ?? 0);

      sums.set(name, (sums.get(name) || 0) + (Number.isFinite(amt) ? amt : 0));
    }

    return Array.from(sums.entries()).map(([name, total]) => ({
      abbr: abbr2(name),
      name,
      salesPrice: total,
    }));
  }, [salesRows]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return <Typography>Error loading data</Typography>;

  return (
    <Box m={{ xs: "10px", md: "20px" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />

        <Button sx={{ backgroundColor: colors.blueAccent[700], color: "#fff" }}>
          <DownloadOutlinedIcon sx={{ mr: 1 }} />
          Download
        </Button>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px"
        mt="20px"
      >
        {/* SALES */}
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
          <StatBox
            title={summary?.sales?.count ?? 0}
            subtitle="Total Sales"
            increase={`${summary?.sales?.totalSales ?? 0}`}
            icon={<PointOfSaleIcon />}
          />
        </Box>

        {/* PURCHASE */}
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
          <StatBox
            title={summary?.purchases?.count ?? 0}
            subtitle="Total Purchases"
            increase={`${summary?.purchases?.totalPurchase ?? 0}`}
            icon={<ShoppingCartOutlinedIcon />}
          />
        </Box>

        {/* EXPENSE */}
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
          <StatBox
            title={summary?.expenses?.count ?? 0}
            subtitle="Expenses"
            increase={`${summary?.expenses?.totalExpense ?? 0}`}
            icon={<AccountBalanceWalletOutlinedIcon />}
          />
        </Box>

        {/* ITEMS */}
        <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
          <StatBox
            title={summary?.items ?? 0}
            subtitle="Items"
            increase={`Warehouses: ${summary?.warehouses ?? 0}`}
            icon={<Inventory2OutlinedIcon />}
          />
        </Box>

        {/* RECENT SALES (SAFE) */}
        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <Typography p={2}>Recent Sales</Typography>

          {(recent?.sales ?? []).map((sale) => (
            <Box key={sale?.id ?? Math.random()} p={2} borderBottom="1px solid #333">
              <Typography>
                {sale?.User?.fullName ?? "Unknown"}
              </Typography>
              <Typography>
                {sale?.Item?.name ?? "Item"} × {sale?.quantity ?? 0}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CHART */}
        <Box gridColumn="span 8" backgroundColor={colors.primary[400]}>
          <LineChart isDashboard />
        </Box>

        {/* BAR */}
        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <BarChart isDashboard data={barData} />
        </Box>

        {/* CAMPAGIN */}
        <Box gridColumn="span 4" backgroundColor={colors.primary[400]}>
          <ProgressCircle size={isSmDown ? 90 : 120} />
          <Typography>
            {summary?.sales?.totalSales ?? 0} generated
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;